import { generate, getUsage, importImage } from 'api/aiImages'
import LargeHeader from 'components/headers/LargeHeader'
import Lottie from 'lottie-react'
import loadingAnimation from '../../img/animations/ai-image-loading.json'
import Panel from 'components/panel/Panel'
import React, { useEffect, useState } from 'react'
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap'
import { useHistory, useLocation } from 'react-router-dom'

const examplePrompts = [
  "A group of friends having a meal at an Italian restaurant. There's a birthday cake on the table.",
  'A sunset over a tropical beach with palm trees swaying in the breeze.',
  'A modern office lobby with a reception desk and potted plants.',
  'A cozy living room with a fireplace, bookshelves, and a comfortable sofa.',
  'A bustling city street at night with neon signs and traffic lights.',
  'A mountain landscape with snow-capped peaks and a clear blue sky.',
]

function AIGenerate() {
  const { search } = useLocation()
  const folderId = new URLSearchParams(search).get('folder_id') || ''
  const routerHistory = useHistory()

  const [prompt, setPrompt] = useState('')
  const [editInstruction, setEditInstruction] = useState('')
  const [historyItems, setHistoryItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingTitle, setLoadingTitle] = useState('')
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState(null)
  const [usage, setUsage] = useState(null)
  const [usageLoading, setUsageLoading] = useState(true)
  const [placeholder, setPlaceholder] = useState('')
  const [aspectRatio, setAspectRatio] = useState('16:9')

  useEffect(() => {
    getUsage()
      .then(({ ok, body }) => {
        if (ok) setUsage(body)
      })
      .finally(() => setUsageLoading(false))
    setPlaceholder(
      `Example prompt: ${examplePrompts[Math.floor(Math.random() * examplePrompts.length)]}`
    )
  }, [])

  const current = historyItems.length ? historyItems[historyItems.length - 1] : null

  const handleGenerate = async () => {
    setLoadingTitle('Generating image')
    setLoading(true)
    setError(null)
    const { ok, body } = await generate({ prompt, history: null, aspectRatio })
    if (!ok) {
      setError(body.message || 'Generation failed. Please try again.')
    } else {
      const entry = {
        prompt,
        image: body.image,
        format: body.format,
        signature: body.signature,
        aspectRatio,
      }
      setHistoryItems([entry])
      setUsage(prev => (prev ? { ...prev, used: prev.used + 1 } : prev))
    }
    setLoading(false)
  }

  const handleApplyEdit = async () => {
    setLoadingTitle('Applying edit')
    setLoading(true)
    setError(null)
    const chain = historyItems
    const { ok, body } = await generate({
      prompt: editInstruction,
      history: chain,
      aspectRatio: current.aspectRatio || aspectRatio,
    })
    if (!ok) {
      setError(body.message || 'Edit failed. Please try again.')
    } else {
      const entry = {
        prompt: editInstruction,
        image: body.image,
        format: body.format,
        signature: body.signature,
        aspectRatio: current.aspectRatio || aspectRatio,
      }
      const next = [...chain, entry]
      setHistoryItems(next)
      setEditInstruction('')
      setUsage(prev => (prev ? { ...prev, used: prev.used + 1 } : prev))
    }
    setLoading(false)
  }

  const handleImport = async () => {
    setImporting(true)
    setError(null)
    const { ok, status, body } = await importImage({
      image: current.image,
      format: current.format,
      prompt: current.prompt,
      folderId,
    })
    if (!ok) {
      setError(
        status === 422
          ? body.message || 'Storage limit reached.'
          : 'Import failed. Please try again.'
      )
    } else {
      routerHistory.push(folderId ? `/media/${folderId}` : '/media')
    }
    setImporting(false)
  }

  const usageBadge = usage ? (
    <span className='badge badge-secondary badge-pill'>
      {usage.limit - usage.used} AI image{' '}
      {usage.limit - usage.used === 1 ? 'generation' : 'generations'} remaining this month
    </span>
  ) : null

  if (usageLoading || loading) {
    return (
      <div
        className='d-flex flex-column align-items-center justify-content-center'
        style={{ padding: '3rem 0' }}>
        {usageLoading ? (
          <>
            <LargeHeader>Create with AI</LargeHeader>
            <Spinner animation='border' role='status' />
          </>
        ) : (
          <>
            <LargeHeader>{loadingTitle}</LargeHeader>
            <Lottie
              animationData={loadingAnimation}
              loop={true}
              autoplay={true}
              style={{ width: 400, height: 400 }}
            />
            <p className='text-muted'>
              Image generation can take up to 60 seconds. Please don&apos;t click back or
              leave the page.
            </p>
          </>
        )}
      </div>
    )
  }

  if (usage?.limit === 0) {
    return (
      <Container style={{ maxWidth: '600px', marginTop: '3rem' }}>
        <Panel>
          <h4>Create with AI</h4>
          <p>
            AI image generation isn&apos;t available on your current package. Speak to
            your account manager to find out more.
          </p>
          <Button variant='secondary' onClick={() => routerHistory.push('/media')}>
            Back to Media
          </Button>
        </Panel>
      </Container>
    )
  }

  if (current === null) {
    return (
      <Container>
        <LargeHeader>Create with AI</LargeHeader>
        {usageBadge && <div className='mb-3'>{usageBadge}</div>}
        {error && <div className='alert alert-danger'>{error}</div>}
        <Row>
          <Col md={8}>
            <label htmlFor='ai-prompt' className='form-label'>
              Describe the image you want to generate. Provide as much detail as you can
              to get the best results.
            </label>
            <textarea
              id='ai-prompt'
              className='form-control mb-3'
              rows={5}
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder={placeholder}
            />
            <label htmlFor='ai-aspect-ratio' className='form-label'>
              Orientation
            </label>
            <select
              id='ai-aspect-ratio'
              className='form-control mb-3'
              value={aspectRatio}
              onChange={e => setAspectRatio(e.target.value)}>
              <option value='16:9'>Landscape (16:9)</option>
              <option value='9:16'>Portrait (9:16)</option>
            </select>
            <Button
              variant='primary'
              onClick={handleGenerate}
              disabled={loading || !prompt.trim()}>
              Generate
            </Button>
          </Col>
        </Row>
      </Container>
    )
  }

  return (
    <Container fluid style={{ maxWidth: '1600px', margin: 'auto' }}>
      <LargeHeader>Create with AI</LargeHeader>
      <Row>
        <Col md={7}>
          <img
            src={`data:${current.format};base64,${current.image}`}
            alt='Generated'
            style={{
              width: '100%',
              height: 'calc(100vh - 320px)',
              objectFit: 'contain',
              borderRadius: '4px',
            }}
          />
          <Button
            variant='success'
            className='w-100 mt-3'
            onClick={handleImport}
            disabled={importing}>
            {importing ? (
              <Spinner as='span' animation='border' size='sm' className='mr-2' />
            ) : null}
            Import to Media Library
          </Button>
        </Col>
        <Col md={5}>
          {usageBadge && <div className='mb-3'>{usageBadge}</div>}
          {error && <div className='alert alert-danger mb-3'>{error}</div>}
          <label className='form-label'>
            Generate an updated image by telling AI what to edit
          </label>
          <textarea
            className='form-control mb-2'
            rows={6}
            value={editInstruction}
            onChange={e => setEditInstruction(e.target.value)}
            placeholder='Describe what to change…'
          />
          <Button
            variant='secondary'
            onClick={handleApplyEdit}
            disabled={loading || !editInstruction.trim()}>
            Apply edit
          </Button>
        </Col>
      </Row>
    </Container>
  )
}

export default AIGenerate
